import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {BuilderStateService} from '../../services/builder-state.service';
import {
  ALL_STATIC_TEXT_FORMATS,
  ARRAY_VALUE_FIELD_TYPES,
  ConditionKey,
  DEFAULT_MASK_FOR_TYPE,
  DefaultToken,
  defaultTokensFor,
  FieldBlock,
  FieldOption,
  FieldType,
  FormBlock,
  isFieldSensitive,
  isSensitiveType,
  ListBlock,
  MaskStyle,
  OPTION_FIELD_TYPES,
  RuleGroup,
  StaticTextBlock,
  StaticTextFormat,
  staticTextFormat,
  supportsCondition,
  supportsDefaultValue
} from '../../../shared/models/form-schema.model';
import {Subscription} from 'rxjs';
import {RuleEditorComponent} from '../rule-editor/rule-editor.component';
import {looksSensitive} from '../../../shared/services/sensitivity-hint';
import {StaticTextComponent} from '../../../runtime/components/static-text/static-text.component';
import {buildFieldLabelMap} from '../../../shared/services/submission-display';
import {SelectComponent} from '../../../../../_ui/components/controls/select/select.component';

/** One field the author may attach a static text to, as the picker shows it: human label over its data key. */
interface FieldChoice {
  key: string;
  label: string;
}

/**
 * One entry of the "+ Feld verknüpfen …" picker.
 *
 * `label` deliberately carries the data key in parentheses behind the human label: the author is building a
 * schema and needs to see which key a note will bind to. The order matters, though — label first — because it
 * is also what the dropdown searches on. That ordering is the whole reason this is a `<cqme-select>` and not a
 * native `<select>`, whose browser type-ahead matches on the leading characters of the option text and so
 * surfaces technical keys ahead of the names the author actually thinks in.
 */
interface FieldPickerChoice {
  key: string;
  label: string;
}

/** One entry of the "Maskierung" picker. */
interface MaskChoice {
  value: MaskStyle;
  label: string;
}

/**
 * Which default-value editor a field type gets. Deliberately coarser than {@link FieldType} — `date` and
 * `time` share one branch (both bind a plain string through the default value accessor, so the input `type`
 * can be bound), while `number` needs its own because Angular's `NumberValueAccessor` is selected off the
 * *static* `type="number"` attribute and would otherwise hand back a string.
 */
type DefaultEditorKind = 'text' | 'number' | 'datetime' | 'boolean' | 'option-single' | 'option-multi';

@Component({
  selector: 'forms-block-editor',
  standalone: true,
  imports: [FormsModule, RuleEditorComponent, StaticTextComponent, SelectComponent],
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 0;
      height: 100%;
    }

    .scrollbar-hide::-webkit-scrollbar {
      display: none;
    }

    .scrollbar-hide {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `],
  template: `
    @if (block) {
      <div class="block-editor flex flex-col h-full bg-white">
        <!-- Header -->
        <div class="p-4 pb-0 flex-shrink-0">
          <div class="flex justify-between items-center mb-4">
            <div class="space-y-1">
              <h2 class="text-xl font-bold text-gray-900 tracking-tight">Eigenschaften</h2>
              <p class="text-[10px] font-bold text-theme-primary-600 uppercase tracking-widest">{{ typeLabel() }}</p>
            </div>
            <button type="button" (click)="close()" aria-label="Schließen"
                    class="p-2 rounded-2xl hover:bg-gray-50 text-gray-500 hover:text-gray-900 transition-all duration-200">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Scrollable Content -->
        <div class="flex-grow overflow-y-auto p-4 pt-0 pb-28 scrollbar-hide"
             (input)="onEdited()" (change)="onEdited()" (click)="onEdited()">
          <div class="space-y-5">
            <!-- Common Properties -->
            <div class="space-y-4">
              <div class="space-y-1.5">
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Anzeige-Label</label>
                <input aria-label="Anzeige-Label" [(ngModel)]="block.label"
                       class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 focus:border-theme-primary-500 transition-all duration-200 sm:text-sm font-medium"/>
              </div>

              @if (hasKey()) {
                <div class="space-y-1.5">
                  <div class="flex items-center justify-between">
                    <label class="block text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Daten-Schlüssel</label>
                    <button type="button" (click)="autoKey()" class="text-[10px] font-bold text-theme-primary-600 hover:underline">
                      Aus Label generieren
                    </button>
                  </div>
                  <input aria-label="Daten-Schlüssel" [(ngModel)]="anyBlock().key"
                         [attr.aria-invalid]="keyRejected()"
                         [class.border-red-300]="keyRejected()"
                         [class.bg-red-50]="keyRejected()"
                         class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 shadow-inner sm:text-sm font-mono text-theme-primary-700"/>
                  @if (keyTaken()) {
                    <p class="text-[10px] text-red-600 font-bold">
                      Dieser Schlüssel wird bereits verwendet – bitte eindeutig wählen.
                    </p>
                  } @else if (keyUnaddressable()) {
                    <!--
                      #470/#481: a dot is the one character that genuinely breaks addressing — the field never
                      renders, rules on it never fire, and its Pflichtfeld-Validator blocks the whole form.
                    -->
                    <p class="text-[10px] text-red-600 font-bold leading-relaxed">
                      Ungültiger Schlüssel: ein Punkt kann nicht aufgelöst werden – das Feld wird nicht
                      angezeigt und blockiert das Formular. Tipp: „Aus Label generieren“.
                    </p>
                  } @else if (keyOffStyle()) {
                    <p class="text-[10px] text-red-600 font-bold leading-relaxed">
                      Neue Schlüssel nur mit Kleinbuchstaben, Ziffern und Unterstrichen – nicht mit „_“ beginnen
                      oder enden. Tipp: „Aus Label generieren“.
                    </p>
                  } @else if (keyLegacyStyle()) {
                    <!-- #481: works fine, just not our naming scheme. A hint, never an error. -->
                    <p class="text-[10px] text-gray-500 font-medium leading-relaxed">
                      System-ID, die in Regeln und API-Übertragungen verwendet wird. Dieser Schlüssel folgt nicht
                      dem üblichen Namensschema – das ist in Ordnung, er funktioniert unverändert weiter.
                    </p>
                  } @else {
                    <p class="text-[10px] text-gray-500 font-medium leading-relaxed">
                      System-ID, die in Regeln und API-Übertragungen verwendet wird.
                    </p>
                  }
                </div>
              }
            </div>

            <!-- Type Specific Settings -->
            @if (isField()) {
              <div class="space-y-4 pt-5 border-t border-gray-50">
                <h3 class="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Konfiguration</h3>

                <!-- Required toggle -->
                <button type="button" role="switch" [attr.aria-checked]="!!field().required"
                        class="flex items-center group text-left w-full"
                        (click)="field().required = !field().required">
                  <div class="w-10 h-6 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out transition-colors"
                       [class.bg-theme-primary-500]="field().required">
                    <div class="bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out"
                         [class.translate-x-4]="field().required"></div>
                  </div>
                  <span class="ml-3 text-sm text-gray-700 font-bold tracking-tight">Pflichtfeld</span>
                </button>

                <!-- Placeholder / help -->
                @if (hasPlaceholder()) {
                  <div class="space-y-1.5">
                    <label class="block text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Platzhalter / Hilfetext</label>
                    <input aria-label="Platzhalter / Hilfetext" [(ngModel)]="field().placeholder"
                           class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                  </div>
                }

                <!-- Default value (#388): a literal the author pre-fills, or a dynamic token resolved at fill time. -->
                @if (supportsDefault()) {
                  <div class="space-y-3 pt-5 border-t border-gray-50" data-testid="default-value-section">
                    <div class="flex items-center justify-between gap-2">
                      <h3 class="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Standardwert</h3>
                      @if (hasDefault()) {
                        <button type="button" (click)="clearDefault()" data-testid="default-reset"
                                class="text-[10px] font-bold text-red-600 hover:text-red-800 uppercase tracking-widest">
                          Zurücksetzen
                        </button>
                      }
                    </div>

                    @switch (defaultEditorKind()) {
                      <!-- date / time: a literal plus the dynamic token that supersedes it -->
                      @case ('datetime') {
                        <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                          <input [type]="block.type" aria-label="Standardwert"
                                 [ngModel]="defaultText()" (ngModelChange)="setDefaultText($event)"
                                 [disabled]="tokenActive()" data-testid="default-literal"
                                 class="block w-full sm:flex-1 min-w-0 px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"/>
                          <button type="button" role="switch" [attr.aria-checked]="tokenActive()"
                                  (click)="toggleDefaultToken()" data-testid="default-token-toggle"
                                  class="flex items-center shrink-0 text-left">
                            <div class="w-10 h-6 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out transition-colors"
                                 [class.bg-theme-primary-500]="tokenActive()">
                              <div class="bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out"
                                   [class.translate-x-4]="tokenActive()"></div>
                            </div>
                            <span class="ml-3 text-sm text-gray-700 font-bold tracking-tight">{{ defaultTokenLabel() }}</span>
                          </button>
                        </div>
                        @if (tokenActive()) {
                          <p class="text-[10px] text-gray-500 font-medium leading-relaxed">{{ defaultTokenHint() }}</p>
                        }
                      }

                      <!-- number: its own branch so Angular's NumberValueAccessor applies and the value stays a number -->
                      @case ('number') {
                        <input type="number" aria-label="Standardwert" placeholder="Kein Standardwert"
                               [ngModel]="defaultNumber()" (ngModelChange)="setDefaultNumber($event)"
                               data-testid="default-literal"
                               class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                      }

                      <!-- boolean: on means the field opens ticked; off means no default at all, not a stored false -->
                      @case ('boolean') {
                        <button type="button" role="switch" [attr.aria-checked]="defaultBool()"
                                (click)="toggleDefaultBool()" data-testid="default-boolean-toggle"
                                class="flex items-center text-left w-full">
                          <div class="w-10 h-6 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out transition-colors"
                               [class.bg-theme-primary-500]="defaultBool()">
                            <div class="bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out"
                                 [class.translate-x-4]="defaultBool()"></div>
                          </div>
                          <span class="ml-3 text-sm text-gray-700 font-bold tracking-tight">Vorbelegt mit „Ja“</span>
                        </button>
                      }

                      <!-- select / radio: exactly one of the field's own options -->
                      @case ('option-single') {
                        @if (defaultOptionList().length) {
                          <!-- Clearing the control IS "kein Standardwert": it emits undefined, which
                               setDefaultOption stores as "no default at all". -->
                          <cqme-select data-testid="default-option-select" class="block w-full"
                                       [options]="defaultOptionChoices()" [labelKey]="'label'" [valueKey]="'value'"
                                       size="md" [clearable]="true" placeholder="— kein Standardwert —"
                                       ariaLabel="Standardwert"
                                       [current]="defaultOption()"
                                       (currentChange)="setDefaultOption($event)"></cqme-select>
                        } @else {
                          <p class="text-[10px] text-gray-500 font-medium leading-relaxed" data-testid="default-no-options">
                            Legen Sie zuerst Optionen an – ein Standardwert lässt sich nur aus den vorhandenen Optionen wählen.
                          </p>
                        }
                      }

                      <!-- multiselect / checkbox-group: any subset of the field's own options -->
                      @case ('option-multi') {
                        @if (defaultOptionList().length) {
                          <div class="space-y-1.5 max-h-48 overflow-y-auto" data-testid="default-option-multi">
                            @for (opt of defaultOptionList(); track $index) {
                              <label class="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-gray-50/50 border border-gray-50 hover:bg-white hover:border-theme-primary-100 cursor-pointer transition-all duration-200">
                                <input type="checkbox" class="h-4 w-4 shrink-0 rounded border-gray-200 text-theme-primary-600 focus:ring-theme-primary-500"
                                       [checked]="isDefaultOptionSelected(opt.value)"
                                       (change)="toggleDefaultMultiOption(opt.value)"/>
                                <span class="text-sm text-gray-700 font-medium truncate">{{ opt.label || opt.value }}</span>
                              </label>
                            }
                          </div>
                        } @else {
                          <p class="text-[10px] text-gray-500 font-medium leading-relaxed" data-testid="default-no-options">
                            Legen Sie zuerst Optionen an – ein Standardwert lässt sich nur aus den vorhandenen Optionen wählen.
                          </p>
                        }
                      }

                      <!-- everything else (text, textarea, e-mail, telefon, IBAN, Steuer-ID, …) -->
                      @default {
                        <input type="text" aria-label="Standardwert" placeholder="Kein Standardwert"
                               [ngModel]="defaultText()" (ngModelChange)="setDefaultText($event)"
                               data-testid="default-literal"
                               class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                      }
                    }

                    <p class="text-[10px] text-gray-500 font-medium leading-relaxed">
                      Wird nur in ein <b>neues</b> Formular vorbelegt. Ein bereits gespeicherter Wert bleibt
                      unverändert – auch ein leerer.
                    </p>

                    <!-- Sensitive + pre-filled: the value lands encrypted in every submission nobody edited. -->
                    @if (showDefaultSensitivityWarning()) {
                      <div class="flex items-start gap-2 rounded-xl bg-yellow-50 border border-yellow-100 p-3"
                           data-testid="default-sensitive-warning">
                        <svg class="h-4 w-4 mt-0.5 flex-shrink-0 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                        </svg>
                        <p class="text-[11px] text-yellow-800 font-medium leading-relaxed">
                          Dieses Feld ist <b>sensibel</b>. Ein Standardwert wird für jede Person, die ihn nicht
                          ändert, verschlüsselt gespeichert – nur setzen, wenn das so gewollt ist.
                        </p>
                      </div>
                    }
                  </div>
                }

                <!-- Sensitivity hint: label/key looks sensitive but the field isn't marked so (non-blocking) -->
                @if (showSensitivityHint()) {
                  <div class="flex items-start gap-2 rounded-xl bg-yellow-50 border border-yellow-100 p-3">
                    <svg class="h-4 w-4 mt-0.5 flex-shrink-0 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                    </svg>
                    <p class="text-[11px] text-yellow-800 font-medium leading-relaxed">
                      Dieses Feld sieht nach sensiblen Daten aus. Erwäge, es als <b>sensibel</b> zu markieren,
                      damit der Wert verschlüsselt und maskiert wird.
                    </p>
                  </div>
                }

                <!-- Sensitive toggle (locked on for inherently-sensitive types) -->
                <button type="button" role="switch" [attr.aria-checked]="fieldIsSensitive()"
                        [disabled]="isAlwaysSensitiveType()"
                        class="flex items-center group text-left w-full disabled:cursor-default"
                        (click)="toggleSensitive()">
                  <div class="w-10 h-6 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out transition-colors"
                       [class.bg-theme-primary-500]="fieldIsSensitive()"
                       [class.opacity-60]="isAlwaysSensitiveType()">
                    <div class="bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out"
                         [class.translate-x-4]="fieldIsSensitive()"></div>
                  </div>
                  <span class="ml-3 text-sm text-gray-700 font-bold tracking-tight">
                    Sensibel (verschlüsselt)
                    @if (isAlwaysSensitiveType()) {
                      <span class="ml-1 text-[10px] font-bold text-gray-500 uppercase tracking-widest">automatisch</span>
                    }
                  </span>
                </button>

                <!-- Mask style (shown when sensitive; read-only for inherently-sensitive types) -->
                @if (fieldIsSensitive()) {
                  <div class="space-y-1.5">
                    <label class="block text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">
                      Maskierung
                      @if (isAlwaysSensitiveType()) {
                        <span class="font-medium normal-case tracking-normal">(schreibgeschützt)</span>
                      }
                    </label>
                    <cqme-select data-testid="mask-style-select" class="block w-full"
                                 [options]="maskChoices" [labelKey]="'label'" [valueKey]="'value'"
                                 size="md" [clearable]="false" [disabled]="isAlwaysSensitiveType()"
                                 placeholder="Maskierung" ariaLabel="Maskierung"
                                 [current]="maskStyle()" (currentChange)="setMaskStyle($event)"></cqme-select>
                    @if (maskStyle() === 'custom' && !isAlwaysSensitiveType()) {
                      <input aria-label="Maskierungs-Muster (RegEx)"
                             [ngModel]="maskPattern()" (ngModelChange)="setMaskPattern($event)"
                             placeholder="RegEx der maskierten Zeichen, z.B. [0-9]"
                             class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm font-mono transition-all"/>
                    }
                    <p class="text-[10px] text-gray-500 font-medium leading-relaxed">
                      Bestimmt, wie der Wert für Nutzer ohne Datenzugriffs-Rolle angezeigt wird. Voller Zugriff
                      erfordert die Rolle „Sensible Daten“.
                    </p>
                  </div>
                }

                <!-- Text length validators -->
                @if (isTextType()) {
                  <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Min. Länge</label>
                      <input type="number" min="0" aria-label="Min. Länge" [(ngModel)]="validators().minLength"
                             class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                    </div>
                    <div class="space-y-2">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Max. Länge</label>
                      <input type="number" min="0" aria-label="Max. Länge" [(ngModel)]="validators().maxLength"
                             class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                    </div>
                  </div>
                }

                <!-- Regex (free text types only) -->
                @if (block.type === 'text' || block.type === 'textarea') {
                  <div class="space-y-1.5">
                    <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Muster (RegEx)</label>
                    <input aria-label="Muster (RegEx)" [(ngModel)]="validators().pattern" placeholder="z.B. ^[A-Z]{2}\\d+$"
                           class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm font-mono transition-all"/>
                  </div>
                }

                <!-- Number min/max -->
                @if (block.type === 'number') {
                  <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Min. Wert</label>
                      <input type="number" aria-label="Min. Wert" [(ngModel)]="validators().min"
                             class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                    </div>
                    <div class="space-y-2">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Max. Wert</label>
                      <input type="number" aria-label="Max. Wert" [(ngModel)]="validators().max"
                             class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                    </div>
                  </div>
                }

                <!-- Date min/max -->
                @if (block.type === 'date') {
                  <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Min. Datum</label>
                      <input type="date" aria-label="Min. Datum" [(ngModel)]="validators().minDate"
                             class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                    </div>
                    <div class="space-y-2">
                      <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Max. Datum</label>
                      <input type="date" aria-label="Max. Datum" [(ngModel)]="validators().maxDate"
                             class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                    </div>
                  </div>
                }

                <!-- File multiple -->
                @if (block.type === 'file') {
                  <button type="button" role="switch" [attr.aria-checked]="!!field().multiple"
                          class="flex items-center group text-left w-full"
                          (click)="field().multiple = !field().multiple">
                    <div class="w-10 h-6 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out transition-colors"
                         [class.bg-theme-primary-500]="field().multiple">
                      <div class="bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out"
                           [class.translate-x-4]="field().multiple"></div>
                    </div>
                    <span class="ml-3 text-sm text-gray-700 font-bold tracking-tight">Mehrere Dateien erlauben</span>
                  </button>
                }

                <!-- Custom error message -->
                <div class="space-y-1.5">
                  <label class="block text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Eigene Fehlermeldung (optional)</label>
                  <input aria-label="Eigene Fehlermeldung" [ngModel]="requiredMessage"
                         (ngModelChange)="requiredMessage = $event; setRequiredMessage($event)"
                         placeholder="z.B. Bitte dieses Feld ausfüllen"
                         class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                </div>
              </div>
            }

            <!-- Static text: format, content, the fields it explains, and a live preview (#501) -->
            @if (block.type === 'static-text') {
              <div class="space-y-5 pt-5 border-t border-gray-50" data-testid="static-text-section">

                <!-- Format. Switching never touches the author's content; only the label and the preview follow. -->
                <div class="space-y-1.5">
                  <label class="block text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Format</label>
                  <div class="flex gap-2" role="radiogroup" aria-label="Format">
                    @for (format of staticTextFormats; track format) {
                      <button type="button" role="radio" [attr.aria-checked]="textFormat() === format"
                              [attr.data-testid]="'static-text-format-' + format"
                              (click)="setTextFormat(format)"
                              class="flex-1 px-3 py-2 rounded-xl text-xs font-bold tracking-tight border transition-all duration-200 active:scale-95"
                              [class.bg-theme-primary-600]="textFormat() === format"
                              [class.text-white]="textFormat() === format"
                              [class.border-theme-primary-600]="textFormat() === format"
                              [class.shadow-md]="textFormat() === format"
                              [class.bg-gray-50]="textFormat() !== format"
                              [class.text-gray-600]="textFormat() !== format"
                              [class.border-gray-100]="textFormat() !== format"
                              [class.hover:bg-white]="textFormat() !== format">
                        {{ textFormatLabel(format) }}
                      </button>
                    }
                  </div>
                  <p class="text-[10px] text-gray-500 font-medium leading-relaxed">{{ textFormatHint() }}</p>
                </div>

                <!-- Content. The label follows the format; the text itself survives every switch. -->
                <div class="space-y-1.5">
                  <label class="block text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">
                    {{ contentLabel() }}
                  </label>
                  <textarea [attr.aria-label]="contentLabel()" [(ngModel)]="staticText().content" rows="10"
                            data-testid="static-text-content"
                            class="block w-full px-3 py-3 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm font-mono leading-relaxed transition-all"></textarea>
                </div>

                <!-- Which fields this text explains. One text may cover several. -->
                <div class="space-y-2" data-testid="static-text-explains">
                  <label class="block text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">
                    Erklärt die Felder
                  </label>

                  @if (explainedFields().length) {
                    <div class="space-y-1.5">
                      @for (choice of explainedFields(); track choice.key) {
                        <div class="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50/50 border border-gray-50">
                          <div class="flex-grow min-w-0">
                            <p class="text-sm font-bold text-gray-700 truncate">{{ choice.label }}</p>
                            <p class="text-[10px] font-mono text-gray-500 truncate">{{ choice.key }}</p>
                          </div>
                          <button type="button" (click)="removeExplains(choice.key)"
                                  [attr.aria-label]="'Verknüpfung mit ' + choice.label + ' entfernen'"
                                  class="p-2 shrink-0 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                            </svg>
                          </button>
                        </div>
                      }
                    </div>
                  }

                  @if (!fieldChoices.length) {
                    <p class="text-[10px] text-gray-500 font-medium leading-relaxed" data-testid="explains-no-fields">
                      Dieses Formular enthält noch keine Felder – legen Sie zuerst ein Feld an, um diesen Text
                      damit zu verknüpfen.
                    </p>
                  } @else if (addableFields().length) {
                    <!--
                      An action, not a value: the picker writes into the block's explains list and then goes
                      back to its placeholder, so the next pick starts from the same state. SelectComponent's
                      reset() is what does that — clearing the bound [current] from here cannot work, and that
                      method documents exactly why.
                      The two-way ngModel plus a second (ngModelChange) trap the repo's lint rule catches does
                      not apply to this markup: cqme-select is driven by [current]/(currentChange) and assigns
                      before it emits, so a handler here never reads a stale value.
                      (No backticks in this comment: the whole template is a TS template literal.)
                    -->
                    <cqme-select #explainsPicker data-testid="static-text-explains-add" class="block w-full"
                                 [options]="explainsChoices()" [labelKey]="'label'" [valueKey]="'key'"
                                 size="md" [clearable]="false" placeholder="+ Feld verknüpfen …"
                                 ariaLabel="Feld verknüpfen"
                                 [current]="noExplainsSelection"
                                 (currentChange)="addExplains(explainsPicker, $event)"></cqme-select>
                  } @else {
                    <p class="text-[10px] text-gray-500 font-medium leading-relaxed" data-testid="explains-all-linked">
                      Alle Felder dieses Formulars sind bereits verknüpft.
                    </p>
                  }

                  <p class="text-[10px] text-gray-500 font-medium leading-relaxed">
                    Der Text erscheint beim Ausfüllen an dieser Stelle – und zusätzlich als Erläuterung unter
                    jedem verknüpften Feld, wenn eine Einreichung angesehen oder als PDF geladen wird.
                  </p>
                </div>

                <!-- Live preview: the runtime component itself, so what is shown here is what will be rendered. -->
                <div class="space-y-2">
                  <h3 class="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Vorschau</h3>
                  <div class="rounded-2xl border border-gray-100 bg-white p-4 pb-0 overflow-x-auto"
                       data-testid="static-text-preview">
                    <forms-static-text [block]="staticText()"></forms-static-text>
                  </div>
                </div>
              </div>
            }

            <!-- List item bounds -->
            @if (block.type === 'list') {
              <div class="grid grid-cols-2 gap-4 pt-5 border-t border-gray-50">
                <div class="space-y-2">
                  <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Min. Einträge</label>
                  <input type="number" min="0" aria-label="Min. Einträge" [(ngModel)]="list().minItems"
                         class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                </div>
                <div class="space-y-2">
                  <label class="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">Max. Einträge</label>
                  <input type="number" min="0" aria-label="Max. Einträge" [(ngModel)]="list().maxItems"
                         class="block w-full px-3 py-2 rounded-xl border-gray-100 bg-gray-50 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 sm:text-sm transition-all"/>
                </div>
              </div>
            }

            <!-- Options (select / multiselect / radio / checkbox-group) -->
            @if (isOptionType()) {
              <div class="space-y-4 pt-5 border-t border-gray-50">
                <div class="flex justify-between items-center">
                  <h3 class="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Optionen</h3>
                  <button type="button" (click)="addOption()" class="text-[10px] font-bold text-theme-primary-600 hover:underline">
                    + NEU HINZUFÜGEN
                  </button>
                </div>
                <div class="space-y-3">
                  @for (opt of (field().options || []); track i; let i = $index) {
                    <div class="flex items-center space-x-2 bg-gray-50/50 p-3 rounded-2xl border border-gray-50 group hover:border-theme-primary-100 hover:bg-white transition-all duration-200">
                      <div class="flex-grow space-y-2 min-w-0">
                        <input [attr.aria-label]="'Bezeichnung der Option ' + (i + 1)" [(ngModel)]="opt.label" placeholder="Bezeichnung"
                               class="block w-full px-2 py-1 bg-transparent border-none text-sm font-bold placeholder-gray-500 focus:ring-0"/>
                        <input [attr.aria-label]="'Wert der Option ' + (i + 1)" [(ngModel)]="opt.value" placeholder="Wert"
                               class="block w-full px-2 py-1 bg-transparent border-none text-[10px] font-mono text-gray-500 placeholder-gray-500 focus:ring-0"/>
                      </div>
                      <button type="button" (click)="removeOption(i)" [attr.aria-label]="'Option ' + (i + 1) + ' entfernen'"
                              class="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover:opacity-100 focus:opacity-100">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Conditional logic -->
            <div class="space-y-5 pt-5 border-t border-gray-50">
              <h3 class="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Bedingte Logik</h3>

              @for (cond of supportedConditions(); track cond.key) {
                <div class="space-y-2">
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-gray-700 tracking-tight">{{ cond.label }}</span>
                    @if (block[cond.key]) {
                      <button type="button" (click)="clearCondition(cond.key)"
                              class="text-[10px] font-bold text-red-600 hover:text-red-800 uppercase tracking-widest">
                        Entfernen
                      </button>
                    }
                  </div>

                  @if (block[cond.key]) {
                    <div class="animate-in fade-in zoom-in-95 duration-200">
                      <forms-rule-editor [ruleGroup]="ruleGroup(cond.key)" [excludeBlockId]="block.id"></forms-rule-editor>
                    </div>
                  } @else {
                    <div>
                      <button type="button" (click)="setupCondition(cond.key)"
                              class="w-full px-4 py-2.5 bg-gray-50 border-2 border-dashed border-gray-100 rounded-xl text-xs font-bold text-theme-primary-700 hover:border-theme-primary-200 hover:bg-white transition-all active:scale-95">
                        + Bedingung definieren
                      </button>
                      <p class="mt-1 text-[10px] text-gray-500 font-medium">{{ cond.hint }}</p>
                    </div>
                  }
                </div>
              }

              <!--
                #491: a condition this block type cannot honour. The schema allows all three on every block, so a
                stored definition may carry one — and it has simply been ignored at runtime ever since. Saying so
                quietly (and offering to clear it) is better than hiding it, and better than dropping the
                author's stored intent behind their back.
              -->
              @for (cond of inertConditions(); track cond.key) {
                <div class="rounded-xl border border-gray-100 bg-gray-50/70 p-3 space-y-1.5">
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-xs font-bold text-gray-600 tracking-tight">{{ cond.label }}</span>
                    <button type="button" (click)="clearCondition(cond.key)"
                            class="text-[10px] font-bold text-gray-500 hover:text-gray-800 uppercase tracking-widest">
                      Entfernen
                    </button>
                  </div>
                  <p class="text-[10px] text-gray-500 font-medium leading-relaxed">
                    Diese Bedingung hat bei „{{ blockTypeName() }}“ keine Wirkung und wird beim Ausfüllen ignoriert.
                    Sie können sie stehen lassen oder entfernen.
                  </p>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Action Footer -->
        <div class="p-4 bg-white/80 backdrop-blur-md border-t border-gray-100 flex space-x-3 z-20 flex-shrink-0">
          <button type="button" (click)="save()" [disabled]="keyRejected()"
                  class="flex-grow py-3 bg-theme-primary-600 text-white rounded-2xl font-bold shadow-xl shadow-theme-primary-100 hover:bg-theme-primary-700 transition-all active:scale-95 disabled:opacity-40">
            Änderungen anwenden
          </button>
          <button type="button" (click)="close()"
                  class="px-6 py-3 bg-gray-50 text-gray-600 rounded-2xl font-bold hover:bg-gray-100 transition-all active:scale-95">
            Abbrechen
          </button>
        </div>
      </div>
    }
  `
})
export class BlockEditorComponent implements OnInit, OnDestroy {
  pageId: string | null = null;
  block: FormBlock | null = null;
  requiredMessage = '';

  /**
   * The block's data key as it was when the panel opened, or null for a key-less block.
   *
   * This is what separates "a legacy key we inherited" from "a key the author is writing right now" (#481), so
   * an existing `fullName` can be saved untouched while typing `fullName` into a new field is still refused.
   */
  private originalKey: string | null = null;

  /** The static-text formats the picker offers, straight from the schema's exhaustive list (#501). */
  readonly staticTextFormats: StaticTextFormat[] = ALL_STATIC_TEXT_FORMATS;

  /**
   * Every field in the definition the panel was opened on, as the `explains` picker shows it.
   *
   * Snapshotted when a block starts being edited rather than recomputed per change-detection pass: the walk is
   * over the whole definition (90 fields in the Personalfragebogen template), and the set cannot change while
   * this panel is open — adding a field means closing it first.
   */
  fieldChoices: FieldChoice[] = [];

  /**
   * The mask styles the "Maskierung" picker offers, in the order the author reads them.
   *
   * A stable array on purpose: `<cqme-select>` hands `[options]` straight to ng-select, which rebuilds its
   * item list whenever that reference changes.
   */
  readonly maskChoices: MaskChoice[] = [
    {value: 'full', label: 'Vollständig (••••)'},
    {value: 'last4', label: 'Letzte 4 sichtbar'},
    {value: 'first4', label: 'Erste 4 sichtbar'},
    {value: 'email', label: 'E-Mail (j••@••.de)'},
    {value: 'iban', label: 'IBAN (letzte 4)'},
    {value: 'custom', label: 'Benutzerdefiniert (RegEx)'}
  ];

  /**
   * What the field-link picker is bound to: nothing, always.
   *
   * The picker is an action — the chosen field is consumed by {@link addExplains} and the control resets — so
   * nothing is ever selected at rest. Binding a typed constant rather than an inline `undefined` is also what
   * tells the template type checker that a pick hands back a field *key*, not a whole option object.
   */
  readonly noExplainsSelection: string | undefined = undefined;

  /** The full catalog. Which of these a given block may actually use comes from the schema — see below. */
  readonly conditions: { key: ConditionKey; label: string; hint: string }[] = [
    {key: 'visibleWhen', label: 'Sichtbar wenn', hint: 'Andernfalls ist das Feld immer sichtbar.'},
    {key: 'enabledWhen', label: 'Aktiviert wenn', hint: 'Andernfalls ist das Feld immer bearbeitbar.'},
    {key: 'requiredWhen', label: 'Pflichtfeld wenn', hint: 'Macht das Feld dynamisch zur Pflicht.'}
  ];

  private sub = new Subscription();

  constructor(public state: BuilderStateService) {
  }

  ngOnInit() {
    this.sub.add(this.state.editingBlock$.subscribe(data => {
      if (data) {
        this.pageId = data.pageId;
        this.block = data.block;
        // #481: a key that came WITH the block is judged differently from one the author types now — a stored
        // `fullName` is fine, typing `fullName` into a fresh field is not.
        this.originalKey = 'key' in data.block ? (data.block as { key: string }).key : null;
        this.requiredMessage = (this.block as FieldBlock)?.validators?.messages?.['required'] ?? '';
        this.fieldChoices = this.collectFieldChoices();
      } else {
        this.pageId = null;
        this.block = null;
        this.originalKey = null;
        this.requiredMessage = '';
        this.fieldChoices = [];
      }
    }));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  // --- type helpers ----------------------------------------------------------
  isField(): boolean {
    const t = this.block?.type;
    return !!t && t !== 'group' && t !== 'list' && t !== 'static-text';
  }

  isOptionType(): boolean {
    return !!this.block && OPTION_FIELD_TYPES.includes(this.block.type as FieldType);
  }

  isTextType(): boolean {
    return ['text', 'textarea', 'email', 'phone', 'iban', 'bank', 'idnumber', 'taxid', 'socialsecurity']
      .includes(this.block?.type ?? '');
  }

  // --- sensitivity / masking -------------------------------------------------
  /** True for the inherently-sensitive semantic types (always encrypted; the toggle is locked on). */
  isAlwaysSensitiveType(): boolean {
    return isSensitiveType(this.block?.type);
  }

  /** Whether the current field is sensitive (catalog type or author-flagged). */
  fieldIsSensitive(): boolean {
    return this.isField() && isFieldSensitive(this.field());
  }

  /** Toggle the author-set sensitive flag (no-op for inherently-sensitive types). */
  toggleSensitive() {
    if (this.isAlwaysSensitiveType()) return;
    const f = this.field();
    f.sensitive = !f.sensitive;
    if (f.sensitive && !f.mask) f.mask = {style: 'full'};
    if (!f.sensitive) f.mask = undefined;
  }

  /** The effective mask style shown in the dropdown (explicit, else the type default, else full). */
  maskStyle(): MaskStyle {
    const f = this.field();
    return f.mask?.style ?? (DEFAULT_MASK_FOR_TYPE[f.type] ?? 'full');
  }

  /**
   * Store the mask style.
   *
   * The parameter is wider than the stored type because `<cqme-select>`'s `currentChange` is typed for the
   * multi-select case as well. Neither an array nor a cleared value can arrive from this single,
   * non-clearable picker, and both are ignored rather than written as a broken mask.
   */
  setMaskStyle(style: MaskStyle | MaskStyle[] | undefined) {
    if (!style || Array.isArray(style)) return;
    const f = this.field();
    f.mask = {...(f.mask ?? {}), style};
  }

  maskPattern(): string {
    return this.field().mask?.pattern ?? '';
  }

  setMaskPattern(pattern: string) {
    const f = this.field();
    f.mask = {...(f.mask ?? {style: 'custom'}), pattern};
  }

  /** Non-blocking nudge: the label/key looks sensitive but the field isn't marked sensitive. */
  showSensitivityHint(): boolean {
    return this.isField()
      && !this.fieldIsSensitive()
      && looksSensitive({label: this.block?.label, key: (this.block as FieldBlock)?.key});
  }

  hasPlaceholder(): boolean {
    return this.isField() && this.block?.type !== 'file' && this.block?.type !== 'signature';
  }

  hasKey(): boolean {
    return !!this.block && 'key' in this.block && this.block.type !== 'static-text';
  }

  field(): FieldBlock {
    return this.block as FieldBlock;
  }

  anyBlock(): any {
    return this.block as any;
  }

  staticText(): StaticTextBlock {
    return this.block as StaticTextBlock;
  }

  list(): ListBlock {
    return this.block as ListBlock;
  }

  // --- static text (#501) ----------------------------------------------------
  /**
   * The effective format of the edited block — its declared `format`, or `html` when it declares none.
   *
   * Resolved through the schema's own {@link staticTextFormat} rather than reading `block.format` here, so the
   * picker highlights **HTML** for a legacy block exactly like the runtime renders it. Two places deciding what
   * "no format" means is how an editor starts disagreeing with the form it is editing.
   */
  textFormat(): StaticTextFormat {
    return staticTextFormat(this.staticText());
  }

  /**
   * Switch the authoring format. The author's `content` is deliberately left **untouched**: the same source is
   * usually readable in both formats (a paragraph of prose is), and silently discarding — or worse,
   * auto-converting — what someone typed because they tapped the wrong segment is not a trade any editor should
   * make. The live preview immediately shows what the choice means, which is the honest way to say it.
   */
  setTextFormat(format: StaticTextFormat) {
    this.staticText().format = format;
  }

  /** The picker's label for a format. */
  textFormatLabel(format: StaticTextFormat): string {
    return format === 'markdown' ? 'Markdown' : 'HTML';
  }

  /** One line saying what the chosen format means for what the author types. */
  textFormatHint(): string {
    return this.textFormat() === 'markdown'
      ? 'Einfache Auszeichnung: **fett**, *kursiv*, # Überschrift, - Liste, [Text](Adresse).'
      : 'HTML-Auszeichnung, z.B. <p>, <strong>, <ul><li> und <a href="…">.';
  }

  /** The content textarea's label, which follows the chosen format. */
  contentLabel(): string {
    return `Inhalt (${this.textFormatLabel(this.textFormat())})`;
  }

  /** The field keys this text explains, always an array so the template never has to guard. */
  explains(): string[] {
    return this.staticText().explains ?? [];
  }

  /**
   * The linked fields, resolved to their human labels, in the order the author added them.
   *
   * A key naming no field in the definition is still listed, under the key itself — the field may have been
   * deleted since, and quietly dropping a stored link would hide the fact that the author has to fix it. That
   * mirrors the read-back side, which ignores a dangling key rather than treating it as an error.
   */
  explainedFields(): FieldChoice[] {
    return this.explains().map(key =>
      this.fieldChoices.find(choice => choice.key === key) ?? {key, label: key});
  }

  /** The fields still available to link — everything in the definition minus what is already linked. */
  addableFields(): FieldChoice[] {
    const linked = new Set(this.explains());
    return this.fieldChoices.filter(choice => !linked.has(choice.key));
  }

  /**
   * The fields still available to link, as the picker shows them.
   *
   * Cached on the set of addable fields rather than mapped afresh, because `<cqme-select>` hands `[options]`
   * straight to ng-select, which rebuilds its internal item list whenever that array's identity changes. A new
   * array on every change-detection pass would do exactly that *while the author has the dropdown open*.
   * Content, not identity, is what invalidates the cache: the labels come from the definition, which the
   * author can still edit elsewhere in the builder.
   */
  explainsChoices(): FieldPickerChoice[] {
    const addable = this.addableFields();
    const key = addable.map(choice => `${choice.key} ${choice.label}`).join('');
    if (key !== this.explainsChoicesKey) {
      this.explainsChoicesKey = key;
      this.explainsChoicesCache = addable.map(choice => ({
        key: choice.key,
        label: `${choice.label} (${choice.key})`
      }));
    }
    return this.explainsChoicesCache;
  }

  private explainsChoicesCache: FieldPickerChoice[] = [];
  /** `null` rather than `''`, so an initially empty option list still populates the cache once. */
  private explainsChoicesKey: string | null = null;

  /**
   * Link one more field, then send the picker back to its placeholder so the next pick starts from the same
   * state — it is an "+ add" action, not a value the control holds on to.
   *
   * The reset has to go through {@link SelectComponent.reset}; assigning `undefined` to the bound `[current]`
   * expression would not reach the view (that method documents why). Guards against an empty pick and against
   * a double-add.
   */
  addExplains(picker: SelectComponent<FieldPickerChoice, string>, key: string | string[] | undefined) {
    picker.reset();
    if (!key || Array.isArray(key)) return;
    const text = this.staticText();
    if (text.explains?.includes(key)) return;
    text.explains = [...(text.explains ?? []), key];
    this.onEdited();
  }

  /**
   * Drop one link. An empty result is written as `undefined` rather than `[]`, so a text the author linked and
   * then unlinked serialises exactly like one that was never linked at all.
   */
  removeExplains(key: string) {
    const text = this.staticText();
    const next = this.explains().filter(entry => entry !== key);
    text.explains = next.length ? next : undefined;
    this.onEdited();
  }

  /**
   * Every field in the current definition, label first, key second — including fields nested inside `group` and
   * `list` blocks, because {@link buildFieldLabelMap} does the same depth-first walk the read-back views use.
   * Reusing it is the point: the picker can only offer keys that a note will actually resolve against later.
   */
  private collectFieldChoices(): FieldChoice[] {
    const labels = buildFieldLabelMap(this.state.currentDefinition);
    return Object.keys(labels).map(key => ({key, label: labels[key]}));
  }

  validators(): NonNullable<FieldBlock['validators']> {
    const f = this.field();
    if (!f.validators) f.validators = {};
    return f.validators;
  }

  typeLabel(): string {
    return `${this.block?.type} Komponente`;
  }

  // --- default value (#388) --------------------------------------------------
  /**
   * Whether the selected block can carry a default value at all. `file` / `signature` cannot — there is no
   * literal an author could meaningfully pre-set for an upload or a drawn signature.
   */
  supportsDefault(): boolean {
    return this.isField() && supportsDefaultValue(this.block?.type);
  }

  /** Which editor variant the selected field type gets (see {@link DefaultEditorKind}). */
  defaultEditorKind(): DefaultEditorKind {
    const t = this.block?.type as FieldType;
    if (t === 'number') return 'number';
    if (t === 'date' || t === 'time') return 'datetime';
    if (t === 'boolean') return 'boolean';
    // multiselect / checkbox-group are option types too, so the array check has to come first.
    if (ARRAY_VALUE_FIELD_TYPES.includes(t)) return 'option-multi';
    if (OPTION_FIELD_TYPES.includes(t)) return 'option-single';
    return 'text';
  }

  /** The dynamic token this field type offers, if any (`today` for a date, `now` for a time). */
  availableDefaultToken(): DefaultToken | null {
    return defaultTokensFor(this.block?.type)[0] ?? null;
  }

  /** German label of the token toggle. */
  defaultTokenLabel(): string {
    return this.availableDefaultToken() === 'now' ? 'Jetzt' : 'Heute';
  }

  /** Explains what the active token resolves to, so "Heute" doesn't read like a fixed date. */
  defaultTokenHint(): string {
    return this.availableDefaultToken() === 'now'
      ? 'Beim Öffnen des Formulars wird automatisch die aktuelle Uhrzeit eingetragen.'
      : 'Beim Öffnen des Formulars wird automatisch das heutige Datum eingetragen.';
  }

  /** Whether the dynamic token currently wins over any literal. */
  tokenActive(): boolean {
    return !!this.field().defaultToken;
  }

  /**
   * Turn the dynamic token on or off. Switching it on drops any literal: `defaultToken` takes precedence over
   * `defaultValue`, and leaving a superseded literal behind is both confusing in the UI and dead weight in the
   * stored definition.
   */
  toggleDefaultToken() {
    const token = this.availableDefaultToken();
    if (!token) return;
    const f = this.field();
    if (f.defaultToken) {
      f.defaultToken = undefined;
    } else {
      f.defaultToken = token;
      f.defaultValue = undefined;
    }
  }

  /** The literal shown in the text/date/time input ('' when there is none). */
  defaultText(): string {
    const v = this.field().defaultValue;
    return v === undefined || v === null ? '' : String(v);
  }

  /** Store a literal, or clear it outright when the author empties the input — never an empty string. */
  setDefaultText(value: string) {
    const f = this.field();
    f.defaultValue = value === '' ? undefined : value;
    if (f.defaultValue !== undefined) f.defaultToken = undefined;
  }

  /** The numeric literal, as `null` when unset (what a `type="number"` input binds to when empty). */
  defaultNumber(): number | null {
    const v = this.field().defaultValue;
    return typeof v === 'number' ? v : null;
  }

  /** Store a numeric literal; an empty or unparseable input clears it rather than storing `NaN`/`''`. */
  setDefaultNumber(value: number | string | null | undefined) {
    const f = this.field();
    if (value === null || value === undefined || value === '') {
      f.defaultValue = undefined;
      return;
    }
    const n = Number(value);
    f.defaultValue = Number.isNaN(n) ? undefined : n;
    if (f.defaultValue !== undefined) f.defaultToken = undefined;
  }

  /** Whether a boolean field opens pre-ticked. */
  defaultBool(): boolean {
    return this.field().defaultValue === true;
  }

  /**
   * Toggle the boolean default. Off means *no default at all* (`undefined`), not a stored `false` — an
   * unticked box is already the natural empty state, so storing `false` would only bloat the definition.
   */
  toggleDefaultBool() {
    const f = this.field();
    f.defaultValue = f.defaultValue === true ? undefined : true;
  }

  /** The field's own options, which are the only values a default may take on an option-based field. */
  defaultOptionList(): FieldOption[] {
    return this.field().options ?? [];
  }

  /**
   * The field's own options as the default-value picker shows them.
   *
   * An option the author has not named yet falls back to its raw value, exactly as the old native `<select>`
   * did — a half-built option must stay pickable and must not render as a blank row. Cached for the same
   * reason as {@link explainsChoices}, and likewise invalidated by content: the author edits these very
   * labels further down the same panel, and the picker has to follow.
   */
  defaultOptionChoices(): FieldOption[] {
    const source = this.defaultOptionList();
    const key = source.map(opt => `${opt.label} ${String(opt.value)}`).join('');
    if (key !== this.defaultOptionChoicesKey) {
      this.defaultOptionChoicesKey = key;
      this.defaultOptionChoicesCache = source.map(opt => ({
        label: opt.label || String(opt.value ?? ''),
        value: opt.value
      }));
    }
    return this.defaultOptionChoicesCache;
  }

  private defaultOptionChoicesCache: FieldOption[] = [];
  /** `null` rather than `''`, so an initially empty option list still populates the cache once. */
  private defaultOptionChoicesKey: string | null = null;

  /** The selected option value; `undefined` means the picker shows its "— kein Standardwert —" placeholder. */
  defaultOption(): any {
    return this.field().defaultValue;
  }

  setDefaultOption(value: any) {
    this.field().defaultValue = value === null || value === undefined ? undefined : value;
  }

  /** The selected values of a multi-pick default (always an array, never `undefined`, for the template). */
  defaultMulti(): any[] {
    const v = this.field().defaultValue;
    return Array.isArray(v) ? v : [];
  }

  isDefaultOptionSelected(value: any): boolean {
    return this.defaultMulti().includes(value);
  }

  /** Add/remove one option from a multi-pick default; an empty selection clears the default entirely. */
  toggleDefaultMultiOption(value: any) {
    const current = this.defaultMulti();
    const next = current.filter(v => v !== value);
    if (next.length === current.length) next.push(value);
    this.field().defaultValue = next.length ? next : undefined;
  }

  /** Whether the author has set anything at all — drives the "Zurücksetzen" affordance and the warning. */
  hasDefault(): boolean {
    if (!this.supportsDefault()) return false;
    const f = this.field();
    return f.defaultToken !== undefined || f.defaultValue !== undefined;
  }

  /** Clear both halves of the default. Written as `undefined` so neither key is serialised at all. */
  clearDefault() {
    const f = this.field();
    f.defaultValue = undefined;
    f.defaultToken = undefined;
  }

  /**
   * Non-blocking warning: a default on a sensitive field is encrypted and stored for every employee who
   * submits without touching it. Legitimate in some forms, surprising in most — so warn, never block.
   */
  showDefaultSensitivityWarning(): boolean {
    return this.supportsDefault() && this.fieldIsSensitive() && this.hasDefault();
  }

  // --- key handling ----------------------------------------------------------
  autoKey() {
    if (!this.block || !('key' in this.block)) return;
    this.anyBlock().key = this.state.slugForLabel(this.block.label || '', this.anyBlock().key);
  }

  keyTaken(): boolean {
    if (!this.block || !('key' in this.block)) return false;
    return this.state.isKeyTaken(this.anyBlock().key, this.block.id);
  }

  /**
   * True when the runtime genuinely cannot address the key — in practice, it contains a dot (#470/#481). A real
   * defect, so it is an error whether the author typed it or inherited it, and it blocks saving.
   *
   * Evaluated on every change detection, not only while typing, so opening an *existing* block whose key is
   * already unresolvable (an imported or hand-edited definition) shows the problem instead of hiding it.
   */
  keyUnaddressable(): boolean {
    if (!this.block || !('key' in this.block)) return false;
    return this.state.isKeyUnaddressable(this.anyBlock().key);
  }

  /**
   * True when the author has **changed** the key to something outside the house charset (#481).
   *
   * Only what the author writes now is held to the charset. A key that arrived with the block is left alone
   * however it looks — `fullName` addresses perfectly well, and refusing to save a stored form because of it
   * would be inventing a defect.
   */
  keyOffStyle(): boolean {
    if (!this.block || !('key' in this.block)) return false;
    const key = this.anyBlock().key;
    return key !== this.originalKey && !this.state.isKeySlug(key);
  }

  /**
   * True for a key that arrived with the block, works fine, and simply does not match the house charset. Shown
   * as a quiet hint — never an error, never blocking. This is the case that used to be painted red (#481).
   */
  keyLegacyStyle(): boolean {
    if (!this.block || !('key' in this.block)) return false;
    const key = this.anyBlock().key;
    return key === this.originalKey && !this.state.isKeySlug(key) && !this.state.isKeyUnaddressable(key);
  }

  /** Everything that must stop a save: a duplicate, an unresolvable key, or a newly typed off-charset one. */
  keyRejected(): boolean {
    return this.keyTaken() || this.keyUnaddressable() || this.keyOffStyle();
  }

  // --- options ---------------------------------------------------------------
  addOption() {
    const f = this.field();
    if (!f.options) f.options = [];
    const n = f.options.length + 1;
    f.options.push({label: `Option ${n}`, value: `option_${n}`});
  }

  removeOption(index: number) {
    this.field().options?.splice(index, 1);
  }

  // --- custom message --------------------------------------------------------
  setRequiredMessage(msg: string) {
    const v = this.validators();
    if (!v.messages) v.messages = {};
    if (msg && msg.trim()) {
      v.messages['required'] = msg.trim();
    } else {
      delete v.messages['required'];
    }
  }

  // --- conditions ------------------------------------------------------------
  /**
   * The conditions this block type can actually honour (#491), from the schema's own matrix — never an inline
   * list here. Offering `Pflichtfeld wenn` on a `group`/`list`/`static-text`, or `Aktiviert wenn` on a
   * `static-text`, invited an author to configure something the runtime ignores.
   */
  supportedConditions(): { key: ConditionKey; label: string; hint: string }[] {
    if (!this.block) return [];
    return this.conditions.filter(c => supportsCondition(this.block!.type, c.key));
  }

  /**
   * Conditions the block **carries** but its type cannot honour — only reachable from a stored definition
   * written before #491 (or hand-edited). Surfaced as a quiet note with a way to clear it, never as an error:
   * the rule is inert, not invalid, so it blocks nothing.
   */
  inertConditions(): { key: ConditionKey; label: string; hint: string }[] {
    if (!this.block) return [];
    return this.conditions.filter(c => !supportsCondition(this.block!.type, c.key) && !!this.block![c.key]);
  }

  /** The block type under the name the palette gives it, for the inert-condition note. */
  blockTypeName(): string {
    const type = this.block?.type ?? '';
    return ({group: 'Gruppe', list: 'Wiederholbare Liste', 'static-text': 'Statischer Text'} as Record<string, string>)[type]
      ?? type;
  }

  ruleGroup(key: ConditionKey): RuleGroup {
    return this.block![key] as RuleGroup;
  }

  setupCondition(key: ConditionKey) {
    if (this.block) this.block[key] = {condition: 'AND', rules: []};
  }

  clearCondition(key: ConditionKey) {
    if (this.block) this.block[key] = undefined;
  }

  // --- actions ---------------------------------------------------------------
  save() {
    if (this.keyRejected()) return;
    if (this.pageId && this.block) {
      this.state.saveEditedBlock(this.pageId, this.block.id, this.block);
    }
  }

  close() {
    this.state.stopEditingBlock();
  }

  /** Refresh the live preview as the user edits (does not commit to the canonical definition). */
  onEdited() {
    this.state.notifyEditingChanged();
  }
}
